import React from 'react';
import styled from 'styled-components';
import classNames from 'classnames';
import GlobalButton from './GlobalButton';
import {
  primaryColor,
  primaryFont,
  greyColor,
  wMedium,
  maxWidth,
  retina,
  wLight,
  maxTablet,
  maxMobile,
  minExtraLargeScreen,
} from '../../../Utils/utilsStyle';
import DoubleCheckIcon from '../../common/assets/svgIcons/DoubleCheckIcon';


type Props = {
  selectedCount: number,
  buttonTitle: string,
  onButtonClick: () => void,
  onClearSelection: () => void,
  itemLabel?: string,
  itemLabelPlural?: string,
  clearLabel?: string,
  loading?: boolean,
  disabled?: boolean,
  hideWhenEmpty?: boolean,
  wrapperClassName?: string,
} & typeof defaultProps;

const defaultProps = {
  itemLabel: 'company',
  itemLabelPlural: 'companies',
  clearLabel: 'Clear selection',
  loading: false,
  disabled: false,
  hideWhenEmpty: true,
  wrapperClassName: '',
};

const Wrapper = styled.div`
  position: fixed;
  bottom: 0px;
  left: 87px;
  right: 0px;
  z-index: 4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 16px;
  padding: 12px 24px;
  background-color: #FFFFFF;
  border-top: 1px solid #E8E8E8;
  box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.06);
  @media all and (min-width: ${minExtraLargeScreen}) {
    padding: 16px 32px;
  }
  @media all and (max-width: ${maxWidth}) , ${retina} {
    padding: 10px 20px;
  }
  @media all and (max-width: ${maxTablet}) {
    left: 0px;
    padding: 8px 16px;
  }
  @media all and (max-width: ${maxMobile}) {
    padding: 8px 12px;
    column-gap: 10px;
  }
  .selection-info {
    display: flex;
    align-items: center;
    column-gap: 10px;
    min-width: 0px;
    @media all and (max-width: ${maxMobile}) {
      column-gap: 6px;
    }
    .selection-tick {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      svg {
        width: 22px;
        height: 22px;
        @media all and (min-width: ${minExtraLargeScreen}) {
          width: 28px;
          height: 28px;
        }
        @media all and (max-width: ${maxTablet}) {
          width: 18px;
          height: 18px;
        }
      }
    }
    .selection-count {
      color: ${greyColor};
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wMedium};
      line-height: normal;
      letter-spacing: -0.52px;
      margin-bottom: 0px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      @media all and (min-width: ${minExtraLargeScreen}) {
        font-size: 20px;
        line-height: 23px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 14px;
        line-height: 16px;
      }
      @media all and (max-width: ${maxMobile}) {
        font-size: 12px;
      }
    }
    .clear-selection {
      color: ${primaryColor};
      font-family: ${primaryFont};
      font-size: 12px;
      font-weight: ${wLight};
      line-height: normal;
      background: none;
      border: none;
      padding: 0px;
      cursor: pointer;
      white-space: nowrap;
      text-decoration: none;
      @media all and (min-width: ${minExtraLargeScreen}) {
        font-size: 14px;
        line-height: 20px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 10px;
      }
      &:hover {
        text-decoration: underline;
      }
    }
  }
  .selection-action {
    flex-shrink: 0;
  }
`;


const SelectionActionFooter = function SelectionActionFooter(props: Props) {
  const {
    selectedCount,
    buttonTitle,
    onButtonClick,
    onClearSelection,
    itemLabel,
    itemLabelPlural,
    clearLabel,
    loading,
    disabled,
    hideWhenEmpty,
    wrapperClassName,
  } = props;

  if (hideWhenEmpty && selectedCount <= 0) return null;

  const label = selectedCount === 1 ? itemLabel : itemLabelPlural;

  return (
    <Wrapper className={classNames('selection-action-footer', wrapperClassName)}>
      <div className="selection-info">
        <span className="selection-tick">
          <DoubleCheckIcon />
        </span>
        <span className="selection-count">{`${selectedCount} ${label} selected`}</span>
        <button
          type="button"
          className="clear-selection"
          onClick={onClearSelection}
        >
          {clearLabel}
        </button>
      </div>
      <div className="selection-action">
        <GlobalButton
          title={buttonTitle}
          onButtonClick={onButtonClick}
          disabled={disabled || selectedCount <= 0}
          loading={loading}
          bgColor={primaryColor}
          textColor="#FFFFFF"
          borderRadius="20px"
          innerSpace="10px 24px"
          fontSize="16px"
          fontSizeSm="14px"
          PaddingSm="8px 20px"
          PaddingXsm="6px 16px"
          PaddingXxsm="6px 12px"
        />
      </div>
    </Wrapper>
  );
};

SelectionActionFooter.defaultProps = defaultProps;

export default SelectionActionFooter;
